<template>
  <span
    class="spm-badge"
    :style="badgeStyle"
    :class="[sizeClass, variantClass]"
  >
    <i
      v-if="icon"
      :class="['spm-badge__icon', icon]"
    />
    <span class="spm-badge__text">{{ label }}</span>
  </span>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import type { BadgeSize, BadgeVariant, BadgeColors } from './displayTypes';

export default defineComponent({
  name: 'SpmBadge',

  props: {
    label: {
      type: String,
      required: true,
    },

    icon: {
      type: String,
      default: '',
    },

    /**
     * Colors can be passed as an object with bg and color properties
     * or use CSS variables via style binding
     */
    colors: {
      type: Object as PropType<BadgeColors>,
      default: null,
    },

    /**
     * Size of the badge: 'sm', 'md', 'lg'
     */
    size: {
      type: String as PropType<BadgeSize>,
      default: 'md',
    },

    /**
     * Variant style: 'filled', 'outlined', 'soft'
     */
    variant: {
      type: String as PropType<BadgeVariant>,
      default: 'soft',
    },

    /**
     * Whether to display text in uppercase
     */
    uppercase: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const badgeStyle = computed(() => {
      if (!props.colors) return {};

      const style: Record<string, string> = {
        '--badge-bg': props.colors.bg,
        '--badge-color': props.colors.color,
      };

      if (props.colors.borderColor) {
        style['--badge-border-color'] = props.colors.borderColor;
      }

      return style;
    });

    const sizeClass = computed(() => `spm-badge--${props.size}`);
    const variantClass = computed(() => {
      const classes = [`spm-badge--${props.variant}`];
      if (props.uppercase) {
        classes.push('spm-badge--uppercase');
      }
      return classes;
    });

    return {
      badgeStyle,
      sizeClass,
      variantClass,
    };
  },
});
</script>

<style lang="scss" scoped>
.spm-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
  background-color: var(--badge-bg, var(--surface-100));
  color: var(--badge-color, var(--text-color));
  border: 1px solid transparent;

  &__icon {
    flex-shrink: 0;
  }

  &__text {
    line-height: 1;
  }

  // Sizes
  &--sm {
    padding: 0.125rem 0.375rem;
    font-size: 0.6875rem;

    .spm-badge__icon {
      font-size: 0.6rem;
    }
  }

  &--md {
    padding: 0.25rem 0.625rem;
    font-size: 0.75rem;

    .spm-badge__icon {
      font-size: 0.7rem;
    }
  }

  &--lg {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;

    .spm-badge__icon {
      font-size: 0.75rem;
    }
  }

  // Variants
  &--filled {
    background-color: var(--badge-bg, var(--primary-500));
    color: var(--badge-color, #ffffff);
  }

  &--outlined {
    background-color: transparent;
    color: var(--badge-color, var(--text-color));
    border-color: var(--badge-border-color, var(--badge-color, var(--surface-300)));
  }

  &--soft {
    background-color: var(--badge-bg, var(--surface-100));
    color: var(--badge-color, var(--text-color));
  }

  // Modifiers
  &--uppercase {
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }
}
</style>
